// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a meeting becomes a Khipu (`docs/MEETINGS_COLLAB_PLAN.md` phase 4;
 * Khipu plan, slice 2, `docs/AYNI_XR_KHIPU_REPLACES_TOURS_PLAN.md`).
 *
 * On the presenter's page only, in memory only, what the room was shown
 * is noted as it happens: the dataset when it changes, the view when the
 * presenter comes to rest on one, each activity's prompt and its end, a
 * question's totals, the right place of a point question, and the pins of
 * an exploration (their notes, never names). On "Save as Khipu" the notes
 * become a `session` Khipu (`buildMeetingKhipu`): the **log** (what
 * happened, with times, replayed by the Khipu player) and a **path** (a
 * step per dataset shown, with its view and questions), so the exchange
 * copy, which carries no log, still plays.
 *
 * Anonymous only (the Khipu plan's privacy rule): no names, no chat, no
 * voice or camera; answers as totals; who acted only as a role.
 *
 * Pure: the clock is a parameter and nothing here touches the DOM, so the
 * whole of it is tested without a browser. Written from scratch for AYNI:
 * nothing here comes from TerraViz's code.
 */

import type { HeldActivity } from './roomClient'
import type { RoomPin, RoomResults } from './roomActivity'
import type { RoomView } from './roomProtocol'
import type { Khipu, KhipuDatasetRef, KhipuEvent, KhipuStep, KhipuText } from './khipu'
import { KHIPU_SCHEMA_VERSION } from './khipu'

export type RecordedEvent =
  | { at: number; kind: 'dataset'; id: string | null }
  | { at: number; kind: 'view'; lat: number; lon: number; zoom: number }
  | { at: number; kind: 'activity'; activity: HeldActivity['kind']; prompt: string }
  | { at: number; kind: 'activityEnd' }
  | { at: number; kind: 'answers'; totals: Record<string, number> }
  | { at: number; kind: 'place'; lat: number; lon: number; label: string }

/** Most events a meeting keeps; past this the oldest views go first. */
export const MAX_EVENTS = 500
/** A view is noted once the camera has stayed on it this long. */
export const VIEW_SETTLE_MS = 2000
/** How long a step of the path lasts when the Khipu plays its path: at least this, at most this. */
export const STEP_MIN_S = 5
export const STEP_MAX_S = 30

const moved = (a: { lat: number; lon: number; zoom: number }, b: { lat: number; lon: number; zoom: number }): boolean =>
  Math.abs(a.lat - b.lat) > 0.5 || Math.abs(((((a.lon - b.lon) + 540) % 360) - 180)) > 0.5 || Math.abs(a.zoom - b.zoom) > 0.2

export interface MeetingRecorder {
  /** What the presenter's sphere shows now (`RoomSyncHandle.snapshot`), sampled about once a second. */
  observe(state: { datasetId: string | null; view: RoomView | null }): void
  /** The room's activity and results (`RoomStatus`), whenever they change. */
  activity(activity: HeldActivity | null, results: RoomResults | null): void
  /** The latest pins of the exploration under way. */
  pins(pins: readonly RoomPin[]): void
  events(): RecordedEvent[]
  /** Whether there is anything worth a Khipu yet. */
  hasContent(): boolean
}

export function createMeetingRecorder(now: () => number = Date.now): MeetingRecorder {
  const events: RecordedEvent[] = []
  let dataset: string | null | undefined
  let lastView: { lat: number; lon: number; zoom: number } | null = null
  let candidate: { lat: number; lon: number; zoom: number; since: number } | null = null
  let activityId: string | null = null
  let activityKind: HeldActivity['kind'] | null = null
  let resultsId: string | null = null
  let latestPins: RoomPin[] = []

  function push(event: RecordedEvent): void {
    events.push(event)
    if (events.length > MAX_EVENTS) {
      const i = events.findIndex((e) => e.kind === 'view')
      events.splice(i >= 0 ? i : 0, 1)
    }
  }

  function endExplore(at: number): void {
    for (const pin of latestPins) push({ at, kind: 'place', lat: pin.lat, lon: pin.lon, label: pin.note })
    latestPins = []
  }

  return {
    observe(state) {
      const at = now()
      if (state.datasetId !== dataset) {
        dataset = state.datasetId
        push({ at, kind: 'dataset', id: state.datasetId })
      }
      const v = state.view
      if (!v) return
      const here = { lat: Math.round(v.lat * 100) / 100, lon: Math.round(v.lon * 100) / 100, zoom: Math.round(v.zoom * 100) / 100 }
      if (!candidate || moved(candidate, here)) {
        candidate = { ...here, since: at }
        return
      }
      if (at - candidate.since >= VIEW_SETTLE_MS && (!lastView || moved(lastView, candidate))) {
        lastView = { lat: candidate.lat, lon: candidate.lon, zoom: candidate.zoom }
        push({ at: candidate.since, kind: 'view', ...lastView })
      }
    },
    activity(activity, results) {
      const at = now()
      if ((activity?.id ?? null) !== activityId) {
        if (activityKind === 'explore') endExplore(at)
        if (activityId !== null) push({ at, kind: 'activityEnd' })
        activityId = activity?.id ?? null
        activityKind = activity?.kind ?? null
        if (activity) push({ at, kind: 'activity', activity: activity.kind, prompt: activity.prompt })
      }
      if (results && results.id !== resultsId) {
        resultsId = results.id
        if (results.kind === 'point') {
          if (results.answer) push({ at, kind: 'place', lat: results.answer[0], lon: results.answer[1], label: activity?.prompt ?? '' })
        } else {
          const totals: Record<string, number> = {}
          results.options.forEach((o, i) => {
            totals[o] = results.counts[i] ?? 0
          })
          push({ at, kind: 'answers', totals })
        }
      }
    },
    pins(pins) {
      if (activityKind === 'explore') latestPins = pins.map((p) => ({ ...p }))
    },
    events: () => events.map((e) => ({ ...e })),
    hasContent: () => events.some((e) => e.kind !== 'dataset' || e.id !== null),
  }
}

export interface MeetingKhipuOptions {
  /** The new Khipu's id: 16 hex characters. */
  id: string
  /** When it is saved (ms). */
  now: number
  title: KhipuText
  /** The language the presenter wrote in (the page's); `und` when unknown. */
  lang?: string
  /** A dataset's title in this app, for the Khipu's dataset list. */
  titleOf?: (datasetId: string) => string | null
}

const iso = (ms: number) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z')

/**
 * The recorded meeting as a `session` Khipu: its log, its datasets by
 * their id in this app (`app:<id>`), and a step for each dataset shown
 * with the view the presenter rested on first and the questions asked
 * meanwhile.
 */
export function buildMeetingKhipu(events: readonly RecordedEvent[], opts: MeetingKhipuOptions): Khipu {
  const ordered = [...events].sort((a, b) => a.at - b.at)
  const start = ordered[0]?.at ?? opts.now
  const keys = new Map<string, string>()
  const datasets: KhipuDatasetRef[] = []
  const keyOf = (id: string): string => {
    let key = keys.get(id)
    if (!key) {
      key = `d${keys.size + 1}`
      keys.set(id, key)
      datasets.push({ key, ref: `app:${id}`, appId: id, title: { en: opts.titleOf?.(id) ?? id } })
    }
    return key
  }

  const log: KhipuEvent[] = []
  let pinN = 0
  for (const e of ordered) {
    const t = Math.max(0, Math.round(e.at - start))
    switch (e.kind) {
      case 'dataset':
        log.push({ t, type: 'dataset', actor: 'presenter', data: { dataset: e.id ? keyOf(e.id) : null } })
        break
      case 'view':
        log.push({ t, type: 'view', actor: 'presenter', data: { lat: e.lat, lon: e.lon, zoom: e.zoom } })
        break
      case 'activity':
        log.push({ t, type: 'activity.start', actor: 'presenter', data: { kind: e.activity, prompt: e.prompt } })
        break
      case 'activityEnd':
        log.push({ t, type: 'activity.end', actor: 'presenter', data: {} })
        break
      case 'answers':
        log.push({ t, type: 'answers', actor: 'system', data: { totals: e.totals } })
        break
      case 'place':
        log.push({ t, type: 'pin.add', actor: 'participant', data: { id: `p${++pinN}`, lat: e.lat, lon: e.lon, ...(e.label ? { note: e.label } : {}) } })
        break
    }
  }

  // The path: a step for each dataset shown, from the moment it went up to the next one.
  const steps: KhipuStep[] = []
  let current: { step: KhipuStep; from: number } | null = null
  const close = (to: number) => {
    if (!current) return
    const seconds = Math.min(STEP_MAX_S, Math.max(STEP_MIN_S, Math.round((to - current.from) / 1000)))
    current.step.advance = { mode: 'timer', seconds }
  }
  for (const e of ordered) {
    if (e.kind === 'dataset' || !current) {
      close(e.at)
      const n = steps.length + 1
      const step: KhipuStep = { id: `s${n}`, n, dataset: e.kind === 'dataset' && e.id ? keyOf(e.id) : null }
      steps.push(step)
      current = { step, from: e.at }
      if (e.kind === 'dataset') continue
    }
    if (e.kind === 'view' && !current.step.view) current.step.view = { lat: e.lat, lon: e.lon, zoom: e.zoom }
    if (e.kind === 'activity' && e.prompt) {
      current.step.prompts = [...(current.step.prompts ?? []), { kind: 'question', audience: 'everyone', text: { [opts.lang ?? 'und']: e.prompt } }]
    }
  }
  close(ordered.at(-1)?.at ?? start)
  if (steps.length === 0) steps.push({ id: 's1', n: 1, dataset: null })

  return {
    format: 'ayni-khipu',
    schemaVersion: KHIPU_SCHEMA_VERSION,
    id: opts.id,
    kind: 'session',
    status: 'published',
    title: opts.title,
    created: iso(opts.now),
    modified: iso(opts.now),
    privacy: { mode: 'anonymous' },
    datasets,
    path: { steps },
    log: { startedAt: iso(start), endedAt: iso(ordered.at(-1)?.at ?? start), events: log },
  }
}
